import React, { useEffect, useState } from "react";
import { AppSetting, AppSettingScopeSchema } from "../../../types/appSettings";
import appSettingsService from "../../../services/appSettingsService";
import { useToast } from "../../../contexts/ToastContext";
import SchemaField from "./SchemaField";

interface SettingsTabProps {
  schema: AppSettingScopeSchema;
  setting: AppSetting;
  onSaved: () => void;
  isDarkMode: boolean;
}

const SettingsTab: React.FC<SettingsTabProps> = ({
  schema,
  setting,
  onSaved,
  isDarkMode,
}) => {
  const toast = useToast();
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormValues({ ...setting.value });
  }, [setting]);

  const handleFieldChange = (name: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormValues({ ...setting.value });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await appSettingsService.updateSettings(schema.scope, formValues);
      toast.success("Settings updated successfully");
      onSaved();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to update settings";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const borderClass = isDarkMode ? "border-rp-separator" : "border-gray-200";
  const surfaceClass = isDarkMode ? "bg-rp-surface" : "bg-white";

  return (
    <div className={`rounded-lg border ${borderClass} ${surfaceClass} p-6`}>
      {/* Header */}
      <div className={`flex items-center justify-between mb-5 pb-4 border-b ${borderClass}`}>
        <div>
          <div className={`text-sm font-medium font-mono ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
            {schema.scope}
          </div>
          <div className={`text-xs mt-0.5 ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`}>
            Last updated by {setting.updatedBy} &middot; {setting.updatedAtUtc}
          </div>
        </div>
        <button
          onClick={handleReset}
          className={`text-sm px-4 py-2 rounded-md border transition-colors ${
            isDarkMode
              ? "border-rp-separator text-rp-grey/50 hover:bg-rp-surface-raised hover:text-rp-grey"
              : "border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          }`}
        >
          Reset
        </button>
      </div>

      {/* Form grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {schema.fields.map((field) => (
          <SchemaField
            key={field.name}
            field={field}
            value={formValues[field.name]}
            onChange={handleFieldChange}
            isDarkMode={isDarkMode}
          />
        ))}
      </div>

      {/* Actions */}
      <div className={`flex justify-end gap-2.5 mt-5 pt-4 border-t ${borderClass}`}>
        <button
          onClick={handleReset}
          className={`text-sm px-5 py-2 rounded-md transition-colors ${
            isDarkMode
              ? "bg-rp-separator text-rp-grey hover:bg-rp-separator/80"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="text-sm px-5 py-2 rounded-md bg-rp-amber text-black hover:bg-rp-amber/80 disabled:opacity-50 transition-colors"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default SettingsTab;
